import { useEffect, useState } from "react";
import { ApprovalList } from "./approvals-table";
import { useToaster } from "../../components/hooks/useToaster";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllApprovals,
  fetchPendingApprovals,
  updateApproval,
  selectAllApprovals,
  selectPendingApprovals,
  selectApprovedApprovals,
  selectDeniedApprovals,
  selectApprovalLoading,
  clearApprovals,
} from "../../reducers/approvalSlice";
import { AppDispatch, useAppDispatch, useAppSelector } from "../../reducers/store";

export function Approvals() {
  const dispatch = useAppDispatch();
  const selectedSupplier = useAppSelector((state) => state.supplier.selectedSupplier);
  const selectedProducingFacilities = useAppSelector((state) => state.supplier.selectedProducingFacilities);

  const [toasterState, openToaster, closeToaster] = useToaster();
  const [isPending, setIsPending] = useState(true);
  const [isApproved, setIsApproved] = useState(false);
  const [isDenied, setIsDenied] = useState(false);
  const [isAll, setIsAll] = useState(false);
  const [isSubLoading, setIsSubLoading] = useState(false);

  const [isApprovedModalOn, setIsApprovedModalOn] = useState(false);
  const [isDeniedModalOn, setIsDeniedModalOn] = useState(false);

  const [selectedApproval, setSelectedApproval] = useState(null as any);
  const [dateFilter, setDateFilter] = useState("30d");

  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [selectedDays, setSelectedDays] = useState("30d");

  // Use selectors to get approval lists
  const allApprovals = useSelector(selectAllApprovals);
  const pendingApprovals = useSelector(selectPendingApprovals);
  const approvedApprovals = useSelector(selectApprovedApprovals);
  const deniedApprovals = useSelector(selectDeniedApprovals);
  const isLoading = useSelector(selectApprovalLoading);

  // useEffect(() => {
  //   getData();
  //   return () => {
  //     dispatch(clearApprovals());
  //   };
  // }, []);

  useEffect(() => {
    if (selectedSupplier) {
      getData();
    }

    return () => {
      dispatch(clearApprovals());
    };
  }, [selectedSupplier]);

  const handleTimeSpanChange = (
    data?: string,
    startDate?: string,
    endDate?: string,
  ) => {
    if (!data) return;

    if (data === "custom" && startDate && endDate) {
      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
      setSelectedDays("custom");
      getData("custom", startDate, endDate, false);
    } else {
      setSelectedDays(data);
      setCustomStartDate("");
      setCustomEndDate("");
      getData(data, undefined, undefined, false);
    }
  };

  async function getData(
    selectedDaysParam?: string,
    startDate?: string,
    endDate?: string,
    updateLoading: boolean = true,
  ) {
    const now = new Date();
    let beforeDate = now.toISOString();
    let afterDate = now.toISOString();

    // Use the passed parameter or fall back to state
    const daysToFetch = selectedDaysParam || selectedDays;

    if (daysToFetch === "60d") {
      const pastDate = new Date(now.setDate(now.getDate() - 60));
      afterDate = pastDate.toISOString();
    } else if (daysToFetch === "30d") {
      const pastDate = new Date(now.setDate(now.getDate() - 30));
      afterDate = pastDate.toISOString();
    } else if (daysToFetch === "90d") {
      const pastDate = new Date(now.setDate(now.getDate() - 90));
      afterDate = pastDate.toISOString();
    } else if (daysToFetch === "custom" && startDate && endDate) {
      beforeDate = endDate;
      afterDate = startDate;
    }

    if (updateLoading) {
      setIsSubLoading(true);
    }

    try {
      const allResult = await dispatch(
        fetchAllApprovals({ before: beforeDate, after: afterDate })
      );
      const pendingResult = await dispatch(fetchPendingApprovals());
      
      if (!allResult.payload || !pendingResult.payload) {
        throw new Error('Failed to fetch approvals');
      }
    } catch (error) {
      console.error("Failed to fetch approvals:", error);
    }

    setIsSubLoading(false);
  }

  const onPendingClick = () => {
    setIsPending(true);
    setIsApproved(false);
    setIsDenied(false);
    setIsAll(false);
  };

  const onApprovedClick = () => {
    setIsApproved(true);
    setIsPending(false);
    setIsDenied(false);
    setIsAll(false);
  };

  const onDeniedClick = () => {
    setIsDenied(true);
    setIsPending(false);
    setIsApproved(false);
    setIsAll(false);
  };

  const onAllClick = () => {
    setIsAll(true);
    setIsPending(false);
    setIsApproved(false);
    setIsDenied(false);
  };

  const onApprovedClicked = (approval: any) => {
    setSelectedApproval(approval);
    setIsApprovedModalOn(true);
  };

  const onApproved = async () => {
    const fullName = localStorage.getItem("user_name");
    const userEmail = localStorage.getItem("userEmail");
    
    try {
      const result = await dispatch(updateApproval({
        id: selectedApproval.id,
        approved: true,
        denialReason: selectedApproval.denialReason,
        approvedTime: moment().utc().format(),
        approvedBy: userEmail ?? undefined,
        approvedByName: fullName ?? undefined,
      }));

      if (result.payload) {
        turnOffApproveModal();
        openToaster("Approval confirmed", "medium", "success");
        getData();
      } else {
        throw new Error('Failed to update approval');
      }
    } catch (err) {
      turnOffApproveModal();
      openToaster(
        "An issue occurred while trying to approve, please try again later",
        "medium",
        "error",
      );
      console.error(JSON.stringify(err));
    }
  };

  const onDeniedClicked = (approval: any) => {
    setSelectedApproval(approval);
    setIsDeniedModalOn(true);
  };

  const onDenied = async () => {
    const fullName = localStorage.getItem("user_name");
    const userEmail = localStorage.getItem("userEmail");
    
    try {
      const result = await dispatch(updateApproval({
        id: selectedApproval.id,
        approved: false,
        denialReason: selectedApproval.denialReason,
        approvedTime: moment().utc().format(),
        approvedBy: userEmail ?? undefined,
        approvedByName: fullName ?? undefined,
      }));

      if (result.payload) {
        turnOffDeniedModal();
        openToaster("Approval confirmed", "medium", "success");
        getData();
      } else {
        throw new Error('Failed to update approval');
      }
    } catch (err) {
      openToaster(
        "An issue occurred while trying to approve, please try again later",
        "medium",
        "error",
      );
      turnOffDeniedModal();
      console.error(JSON.stringify(err));
    }
  };

  const turnOffApproveModal = () => {
    setSelectedApproval(null);
    setIsApprovedModalOn(false);
  };

  const turnOffDeniedModal = () => {
    setSelectedApproval(null);
    setIsDeniedModalOn(false);
  };

  const onDeniedReasonChange = (e: any) => {
    let tempSelectedApproval = selectedApproval;
    tempSelectedApproval.denialReason = e.target.value;
    setSelectedApproval(tempSelectedApproval);
  };

  return (
    <div className="flex max-h-[100%] min-h-[100%] w-[100%] flex-row gap-x-[10px] text-[black]">
      {/* Page Body */}
      <div className="w-[100%] bg-white">
        {/* Tab Container */}
        <div className="border-b-2 border-[#e5e7eb]">
          <div className="flex gap-[20px]">
            <button
              className={`p-3 ${isPending ? "mb-[-2px] border-b-2 border-[#fedd00] font-bold" : ""}`}
              onClick={() => onPendingClick()}
            >
              Pending Approval
            </button>
            <button
              className={`p-3 ${isApproved ? "mb-[-2px] border-b-2 border-[#fedd00] font-bold" : ""}`}
              onClick={() => onApprovedClick()}
            >
              Approved
            </button>
            <button
              className={`p-3 ${isDenied ? "mb-[-2px] border-b-2 border-[#fedd00] font-bold" : ""}`}
              onClick={() => onDeniedClick()}
            >
              Denied
            </button>
            <button
              className={`p-3 ${isAll ? "mb-[-2px] border-b-2 border-[#fedd00] font-bold" : ""}`}
              onClick={() => onAllClick()}
            >
              All
            </button>
          </div>
        </div>
        {!isLoading && (
          <div
            className="overflow-scroll"
            style={{ height: "calc(100% - 50px)" }}
          >
            {isPending && (
              <div>
                <ApprovalList
                  approvalList={pendingApprovals.filter((approval: any) => Number(approval.supplier?.id ?? 0) === Number(selectedSupplier?.id ?? 0) || selectedProducingFacilities.map((facility: any) => Number(facility.id ?? 0)).includes(Number(approval.supplier?.id ?? 0)))}
                  listType="pending"
                  onApprove={onApprovedClicked}
                  onDenied={onDeniedClicked}
                  isSubLoading={isSubLoading}
                  setIsSubLoading={setIsSubLoading}
                  handleButtonClick={handleTimeSpanChange}
                />
              </div>
            )}
            {isApproved && (
              <div>
                <ApprovalList
                  approvalList={approvedApprovals.filter((approval: any) => Number(approval.supplier?.id ?? 0) === Number(selectedSupplier?.id ?? 0) || selectedProducingFacilities.map((facility: any) => Number(facility.id ?? 0)).includes(Number(approval.supplier?.id ?? 0)))}
                  listType="approved"
                  onApprove={onApprovedClicked}
                  onDenied={onDeniedClicked}
                  isSubLoading={isSubLoading}
                  setIsSubLoading={setIsSubLoading}
                  handleButtonClick={handleTimeSpanChange}
                />
              </div>
            )}
            {isDenied && (
              <div>
                <ApprovalList
                  approvalList={deniedApprovals.filter((approval: any) => Number(approval.supplier?.id ?? 0) === Number(selectedSupplier?.id ?? 0) || selectedProducingFacilities.map((facility: any) => Number(facility.id ?? 0)).includes(Number(approval.supplier?.id ?? 0)))}
                  listType="denied"
                  onApprove={onApprovedClicked}
                  onDenied={onDeniedClicked}
                  isSubLoading={isSubLoading}
                  setIsSubLoading={setIsSubLoading}
                  handleButtonClick={handleTimeSpanChange}
                />
              </div>
            )}
            {isAll && (
              <div>
                <ApprovalList
                  approvalList={allApprovals.filter((approval: any) => Number(approval.supplier?.id ?? 0) === Number(selectedSupplier?.id ?? 0) || selectedProducingFacilities.map((facility: any) => Number(facility.id ?? 0)).includes(Number(approval.supplier?.id ?? 0)))}
                  listType="all"
                  onApprove={onApprovedClicked}
                  onDenied={onDeniedClicked}
                  isSubLoading={isSubLoading}
                  setIsSubLoading={setIsSubLoading}
                  handleButtonClick={handleTimeSpanChange}
                />
              </div>
            )}
          </div>
        )}
        {isLoading && (
          <div className="absolute inset-0 z-10 mt-48 flex items-center justify-center bg-white bg-opacity-50 backdrop-blur-sm">
            <span className="loading loading-spinner h-[40px] w-[40px] text-tqsYellow"></span>
          </div>
        )}
      </div>
      {isApprovedModalOn && (
        <div className="fixed left-0 top-0 z-[999] h-[100vh] w-[100vw] bg-[#08080880]">
          <div
            className="absolute z-[9999] h-[auto] w-[500px] rounded-[16px] border-2 border-tqsYellow bg-white p-[10px]"
            style={{ top: "calc(50% - 85px)", left: "calc(50% - 250px)" }}
          >
            <div className="flex justify-end">
              <div onClick={() => turnOffApproveModal()}>
                <FontAwesomeIcon
                  icon={faXmark}
                  className="mr-[4px] text-[24px]"
                />
              </div>
            </div>
            <div className="p-[10px]">
              <p>
                Are you sure you want to approve{" "}
                {selectedApproval.createUser.firstName}{" "}
                {selectedApproval.createUser.lastName}'s request for{" "}
                <b>{selectedApproval.hours} hours</b> on{" "}
                {moment(selectedApproval.time).format("MMM Do YYYY")}?{" "}
              </p>
            </div>
            <div className="mt-[10px] flex justify-between border-t border-gray-300 p-[10px]">
              <button
                className="rounded-md bg-[#555555] px-8 py-1 text-sm font-semibold text-white"
                onClick={() => turnOffApproveModal()}
              >
                Cancel
              </button>
              <button
                className="rounded-md bg-tqsYellow px-8 py-1 text-sm font-semibold text-black"
                onClick={() => onApproved()}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeniedModalOn && (
        <div className="fixed left-0 top-0 z-[999] h-[100vh] w-[100vw] bg-[#08080880]">
          <div
            className="absolute z-[9999] h-[auto] w-[500px] rounded-[16px] border-2 border-tqsYellow bg-white p-[10px]"
            style={{ top: "calc(50% - 190px)", left: "calc(50% - 250px)" }}
          >
            <div className="flex justify-end">
              <div onClick={() => turnOffDeniedModal()}>
                <FontAwesomeIcon
                  icon={faXmark}
                  className="mr-[4px] text-[24px]"
                />
              </div>
            </div>
            <div className="p-[15px]">
              <p className="mb-[10px]">
                Please proved a reason for declining{" "}
                {selectedApproval.createUser.firstName}{" "}
                {selectedApproval.createUser.lastName}'s request for{" "}
                <b>{selectedApproval.hours} hours</b> on{" "}
                {moment(selectedApproval.time).format("MMM Do YYYY")}?{" "}
              </p>
              <textarea
                className="w-[100%] border border-gray-300 bg-white p-[10px]"
                onChange={onDeniedReasonChange}
              ></textarea>
            </div>
            <div className="mt-[10px] flex justify-between border-t border-gray-300 p-[10px]">
              <button
                className="rounded-md bg-[#555555] px-8 py-1 text-sm font-semibold text-white"
                onClick={() => turnOffDeniedModal()}
              >
                Cancel
              </button>
              <button
                className="rounded-md bg-tqsYellow px-8 py-1 text-sm font-semibold text-black"
                onClick={() => onDenied()}
              >
                Deny
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
